import { memo, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { setSelectedJobsiteIdWithBuilder } from "utilities/jobPicker/jobPicker";

import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { ColorSwatch } from "commonComponents/entity/colorPicker/ColorSwatch/ColorSwatch";
import { type AccountSwitcherItem } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import {
    JobIdTypes,
    JobPickerDisplayModes,
} from "commonComponents/utilities/JobPicker/JobPicker.types";
import { JobIcon } from "commonComponents/utilities/JobPicker/Sections/JobIcon/JobIcon";
import {
    ALL_JOBS,
    type IJobListItemProps,
} from "commonComponents/utilities/JobPicker/Sections/JobsList/JobsList.types";

import { JobStatus } from "entity/job/Job.api.types";

import "./JobListItem.less";

import { JobInfo } from "../JobInfoSection/JobInfoSection";

interface IBuilderLabelProps {
    id: number;
    accounts?: AccountSwitcherItem[];
}

const BuilderLabel = ({ id, accounts }: IBuilderLabelProps) => {
    if (!accounts || accounts.filter((b) => b.selected).length <= 1) {
        return null;
    }
    const name = accounts.find((b) => b.builderId === id)?.builderName;
    if (!name) {
        return null;
    }
    return (
        <div className="grow text-sm font-normal font-sans -mt-0.5 whitespace-nowrap text-ellipsis overflow-hidden text-gray-70">
            {name}
        </div>
    );
};

export const JobListItem = memo(function JobListItem({
    currentJob,
    displayMode,
    selectedJobIds,
    isTemplateMode,
    availableAccounts,
    showAccountingOnTimeClock,
    draftJobIds,
    keywordSearch,
    onJobPickerJobChanged,
}: IJobListItemProps) {
    const scheduleMode = displayMode === JobPickerDisplayModes.Calendar;

    const [isPopoverVisible, setPopoverVisible] = useState(false);

    let currentlySelectedId: number;
    if (typeof selectedJobIds === "number") {
        currentlySelectedId = selectedJobIds;
    } else if (selectedJobIds === null) {
        currentlySelectedId = JobIdTypes.NoJobs;
    } else {
        currentlySelectedId = JobIdTypes.AllJobs;
    }

    let content = (
        <>
            <div className="flex flex-row items-center justify-between w-full">
                <div
                    className={cn(
                        "h-6 min-w-0 overflow-hidden max-xs:text-lg max-xs:h-8 gap-3 flex flex-row items-center font-sans",
                        {
                            "text-sm font-normal text-gray-90": currentJob.jobId !== ALL_JOBS,
                            "font-semibold text-gray-70 text-xs uppercase":
                                currentJob.jobId !== currentlySelectedId &&
                                currentJob.jobId === ALL_JOBS,
                            "font-semibold text-blue-70 text-xs uppercase":
                                currentJob.jobId === ALL_JOBS && currentlySelectedId === ALL_JOBS,
                        },
                    )}
                >
                    {currentJob.jobId !== ALL_JOBS && scheduleMode && (
                        <ColorSwatch
                            className="job-list-item-color-swatch m-0 p-0"
                            size={14}
                            color={currentJob.jobInfo.color}
                        />
                    )}
                    <div
                        className={cn(
                            "grow min-w-0 whitespace-nowrap overflow-hidden text-ellipsis",
                        )}
                    >
                        {currentJob.jobName}
                    </div>
                </div>
                <JobIcon
                    currentJob={currentJob}
                    displayMode={displayMode}
                    showAccountingOnTimeClock={showAccountingOnTimeClock}
                    draftJobIds={draftJobIds}
                />
            </div>
            <BuilderLabel id={currentJob.builderId} accounts={availableAccounts} />
        </>
    );

    if (currentJob.jobId > 0 && !isTemplateMode && currentJob.jobStatus !== JobStatus.GeneralJob) {
        content = (
            <BTPopover
                visible={isPopoverVisible}
                content={
                    isPopoverVisible ? (
                        <JobInfo
                            currentJob={currentJob}
                            isTemplateMode={isTemplateMode}
                            isPopover
                        />
                    ) : null
                }
                placement="right"
            >
                {content}
            </BTPopover>
        );
    }

    const handleJobChanged = async () => {
        if (currentJob.jobId !== currentlySelectedId) {
            await onJobPickerJobChanged(currentJob, keywordSearch);
            setSelectedJobsiteIdWithBuilder(currentJob.jobId, currentJob.builderId);
        }
    };

    return (
        <div className="flex flex-row items-center px-4">
            <div
                className={cn(
                    "rounded-md inline-block cursor-pointer w-full hover:bg-blue-10 hover:no-underline p-2",
                    {
                        "bg-blue-10 border-none": currentJob.jobId === currentlySelectedId,
                        "py-3": currentJob.jobId === ALL_JOBS,
                    },
                )}
                onClick={handleJobChanged}
                onMouseEnter={() => setPopoverVisible(true)}
                onMouseLeave={() => setPopoverVisible(false)}
                data-testid={`JobListItem-${currentJob.jobId}`}
            >
                {content}
            </div>
        </div>
    );
});
